import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Shield, Clock, Award, CircleCheckBig, ArrowRight, Mail } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIFFERENTIATORS = [
  { icon: Shield, title: "Proven Results", desc: "We guarantee radon levels below Health Canada's guideline of 200 Bq/m\u00B3. If we can't achieve safe levels, we don't charge you." },
  { icon: Clock, title: "Fast Installation", desc: "Most systems installed in one day with minimal disruption. We respect your time and keep your home clean throughout the process." },
  { icon: Award, title: "Quality Equipment", desc: "We use only reliable, energy-efficient fans and materials. Our systems are built to last and operate quietly." },
  { icon: CircleCheckBig, title: "Complete Service", desc: "From testing to installation to follow-up testing, we handle everything. You get one team, clear communication, and peace of mind." },
];

export default function GetQuotePage() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", homeSize: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/get-quote`, form);
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      alert("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main>
      <div className="min-h-screen">
        <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-blue-50">
          <div className="max-w-7xl mx-auto text-center">
            <Badge className="mb-4 bg-green-100 text-green-700 hover:bg-green-100 text-lg px-6 py-2">
              Upfront, Honest Pricing
            </Badge>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Professional Radon Mitigation<span className="text-blue-600"> Starting at $1,800</span>
            </h1>
            <p className="text-2xl text-gray-600 max-w-3xl mx-auto mb-6">
              Calgary's trusted radon mitigation specialists
            </p>
          </div>
        </section>

        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Pricing Card */}
            <Card className="border-2 border-blue-100 shadow-xl mb-12" data-testid="pricing-card">
              <CardHeader className="bg-gradient-to-br from-blue-50 to-white text-center">
                <CardTitle className="text-3xl mb-2">Radon Mitigation Pricing</CardTitle>
                <p className="text-gray-600">For Calgary Residential Homes</p>
              </CardHeader>
              <CardContent className="p-8">
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-lg font-semibold text-gray-900">Starting Price</span>
                    <span className="text-3xl font-bold text-green-600">$1,800</span>
                  </div>
                </div>
                <div className="space-y-4 mb-6">
                  {[
                    { item: "Initial Assessment", price: "Free" },
                    { item: "Radon Testing", price: "Free" },
                    { item: "Post-Mitigation Testing", price: "Free" },
                  ].map((row) => (
                    <div key={row.item} className="flex justify-between items-center py-3 border-b">
                      <span className="text-gray-700">{row.item}</span>
                      <span className="font-semibold text-green-600">{row.price}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center py-3">
                    <span className="text-gray-700">System Warranty</span>
                    <span className="font-semibold text-blue-600">Included</span>
                  </div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Every home is unique. We provide a detailed quote after inspecting your property to ensure accurate pricing. Our team evaluates your home's specific needs to give you a fair and transparent price.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* What Makes Us Different */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">What Makes Us Different</h2>
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {DIFFERENTIATORS.map((d) => (
                  <Card key={d.title} className="border-2 hover:border-blue-200 transition-colors">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="bg-blue-100 p-3 rounded-lg flex-shrink-0">
                          <d.icon className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">{d.title}</h3>
                          <p className="text-gray-600 text-sm">{d.desc}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Quote Form */}
            <Card className="border-2 border-blue-100 shadow-xl" data-testid="quote-form-card">
              <CardHeader className="bg-gradient-to-br from-blue-50 to-white">
                <CardTitle className="text-2xl">Request Your Free Quote</CardTitle>
                <p className="text-gray-600 mt-2">Fill out the form and we'll contact you within 24 hours with a detailed quote</p>
              </CardHeader>
              <CardContent className="p-8">
                {submitted ? (
                  <div className="text-center py-12" data-testid="quote-success">
                    <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Shield className="h-8 w-8 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
                    <p className="text-gray-600">We'll contact you within 24 hours with a detailed quote.</p>
                  </div>
                ) : (
                  <form className="space-y-6" onSubmit={handleSubmit} data-testid="quote-form">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-sm font-semibold text-gray-700">Full Name *</Label>
                      <Input id="name" name="name" required placeholder="John Smith" value={form.name} onChange={handleChange} className="border-2 focus:border-blue-500" data-testid="quote-name" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-sm font-semibold text-gray-700">Phone Number *</Label>
                      <Input id="phone" name="phone" required placeholder="(403) 555-1234" type="tel" value={form.phone} onChange={handleChange} className="border-2 focus:border-blue-500" data-testid="quote-phone" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-semibold text-gray-700">Email Address *</Label>
                      <Input id="email" name="email" required placeholder="john@example.com" type="email" value={form.email} onChange={handleChange} className="border-2 focus:border-blue-500" data-testid="quote-email" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="address" className="text-sm font-semibold text-gray-700">Property Address *</Label>
                      <Input id="address" name="address" required placeholder="123 Main Street SW, Calgary, AB" value={form.address} onChange={handleChange} className="border-2 focus:border-blue-500" data-testid="quote-address" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="homeSize" className="text-sm font-semibold text-gray-700">Approximate Home Size (sq ft)</Label>
                      <Input id="homeSize" name="homeSize" placeholder="e.g., 2000 sq ft" value={form.homeSize} onChange={handleChange} className="border-2 focus:border-blue-500" data-testid="quote-home-size" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-sm font-semibold text-gray-700">Additional Information (Optional)</Label>
                      <Textarea id="message" name="message" placeholder="Tell us about your radon concerns or any questions you have" rows={4} value={form.message} onChange={handleChange} className="border-2 focus:border-blue-500 resize-none" data-testid="quote-message" />
                    </div>
                    <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-6 text-lg" disabled={submitting} data-testid="quote-submit-btn">
                      {submitting ? "Submitting..." : "Get My Free Quote"} {!submitting && <ArrowRight className="ml-2 h-5 w-5" />}
                    </Button>
                    <p className="text-sm text-gray-600 text-center">
                      By submitting this form, you agree to be contacted by ZARC Radon Solutions regarding your quote request.
                    </p>
                  </form>
                )}
              </CardContent>
            </Card>

            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-4">Prefer to email us?</p>
              <div className="flex justify-center">
                <a href="mailto:info.zarcradon@gmail.com" className="flex items-center justify-center space-x-2 text-blue-600 hover:text-blue-700" data-testid="quote-email-link">
                  <Mail className="h-5 w-5" />
                  <span className="font-semibold">info.zarcradon@gmail.com</span>
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
